import { useSyncExternalStore } from 'react';
import { useWorkStatusStore } from '../store/workStatusStore';
import { WORK_STATUS_TONE, type WorkStatusTone } from '../lib/work-status';
import { tLayout, getLayoutLang, subscribeLayoutLang } from '../lib/layout-i18n';

/**
 * 내 근무 상태 → 프로필 아바타 점(2026-08-12 사용자 지적).
 *
 *   "상태바의 상태를 수정하였는데 프로필의 상태 표시 색이 변하지 않는다"
 *
 * 원인: 프로필 아바타의 점은 `status="online"` **하드코딩**이었다(우측 레일·캘린더 레일·대시보드
 * '내 프로필' 위젯 3곳). 근무 상태 스토어와 아예 연결돼 있지 않아 무엇을 고르든 초록이었다.
 *
 * 색은 상태바와 **같은 tone 이름**을 쓴다(`lib/work-status.WORK_STATUS_TONE`) — 같은 상태가 두
 * 자리에서 다른 색으로 보이면 사용자가 어느 쪽을 믿어야 할지 알 수 없다.
 *
 * 미설정(null)이면 `tone` 을 주지 않는다 → 호출부는 **종전 색(online 초록)** 을 그대로 쓴다.
 * 아직 아무것도 고르지 않은 사용자의 화면을 조용히 회색으로 바꾸지 않기 위한 무회귀 선택이다.
 *
 * ⚠ 서버 근태 조회(`hydrate`)는 상태바(`StatusBar.WorkStatusItem`)가 mount 시 1회 담당한다.
 *   이 훅은 **읽기 전용** — 아바타가 뜰 때마다 근태 API 를 두드리지 않는다.
 */
export interface MyWorkStatusDot {
  /** 근무 상태 색 계열. 미설정이면 undefined(= 호출부 기본색 유지). */
  tone?: WorkStatusTone;
  /** 표시용 라벨(툴팁·aria) — 미설정이면 "상태". */
  label: string;
}

export function useMyWorkStatusDot(): MyWorkStatusDot {
  // 언어 전환 시 라벨도 따라 바뀌게 — StatusDropdown 과 같은 구독 방식.
  useSyncExternalStore(subscribeLayoutLang, getLayoutLang, getLayoutLang);
  const status = useWorkStatusStore((s) => s.status);

  if (!status) return { label: tLayout('layout.workstatus.none') };
  return { tone: WORK_STATUS_TONE[status], label: tLayout(`layout.workstatus.${status}`) };
}
